import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';
import { PRODUCTION_URL } from '../productionLink/productionLink';

const KitchenOperations = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeOrders, setActiveOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [selectedStation, setSelectedStation] = useState('all');
  const [stations, setStations] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');

  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444'
  };

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [selectedStation]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([fetchOrders(), fetchStats(), fetchStations()]);
    setLoading(false);
  };

  const fetchOrders = async () => {
    try {
      const data = await apiGet('/admin/kitchen/operations/active-orders');
      if (data.success) {
        let filtered = data.data;
        if (selectedStation !== 'all') {
          filtered = filtered.filter(order => order.station_id == selectedStation);
        }
        setActiveOrders(filtered);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await apiGet('/admin/kitchen/operations/stats');
      if (data.success) setStats(data.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchStations = async () => {
    try {
      const data = await apiGet('/admin/kitchen/setup/stations');
      if (data.success) setStations(data.data.filter(s => s.is_active));
    } catch (error) {
      console.error('Error fetching stations:', error);
    }
  };

  const updateItemStatus = async (itemId, newStatus, stationId = null, staffId = null) => {
    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/kitchen/operations/items/${itemId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          item_status: newStatus,
          station_id: stationId,
          assigned_to_staff_id: staffId
        })
      });

      const data = await response.json();
      if (data.success) {
        setSuccessMessage(`✅ Item status updated to ${newStatus}`);
        fetchOrders();
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      pending: colors.warning,
      preparing: colors.accent,
      ready: colors.success,
      served: colors.textLight
    };
    return statusColors[status] || colors.textLight;
  };

  const getTimeSince = (dateString) => {
    const diffMs = Date.now() - new Date(dateString);
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    const diffHours = Math.floor(diffMins / 60);
    return `${diffHours}h ${diffMins % 60}m ago`;
  };

  const styles = {
    container: {
      padding: isMobile ? '16px 4px' : '24px',
      maxWidth: '1400px',
      margin: '0 auto'
    },
    header: {
      marginBottom: isMobile ? '16px' : '24px'
    },
    title: {
      fontSize: isMobile ? '20px' : '28px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: '6px'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)',
      gap: isMobile ? '12px' : '16px',
      marginBottom: isMobile ? '20px' : '24px'
    },
    statCard: {
      background: colors.white,
      padding: isMobile ? '16px' : '20px',
      borderRadius: '12px',
      boxShadow: '0 4px 12px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`,
      textAlign: 'center'
    },
    statValue: {
      fontSize: isMobile ? '24px' : '32px',
      fontWeight: '800',
      marginBottom: '4px'
    },
    statLabel: {
      fontSize: isMobile ? '11px' : '12px',
      color: colors.textLight,
      textTransform: 'uppercase',
      fontWeight: '600'
    },
    filterBar: {
      background: colors.white,
      padding: isMobile ? '12px' : '16px',
      borderRadius: '12px',
      marginBottom: isMobile ? '16px' : '20px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
      display: 'flex',
      gap: '12px',
      flexWrap: 'wrap',
      alignItems: 'center'
    },
    filterButton: {
      padding: isMobile ? '8px 14px' : '10px 18px',
      borderRadius: '8px',
      border: 'none',
      fontSize: isMobile ? '12px' : '13px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    ordersGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
      gap: isMobile ? '12px' : '16px'
    },
    orderCard: {
      background: colors.white,
      padding: isMobile ? '16px' : '20px',
      borderRadius: '12px',
      boxShadow: '0 4px 12px rgba(45, 80, 22, 0.08)',
      border: `2px solid ${colors.mediumGray}`,
      position: 'relative'
    },
    button: {
      padding: isMobile ? '8px 12px' : '10px 16px',
      borderRadius: '6px',
      border: 'none',
      fontSize: isMobile ? '11px' : '12px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    successMessage: {
      position: 'fixed',
      top: '20px',
      right: '20px',
      background: colors.success,
      color: colors.white,
      padding: '12px 20px',
      borderRadius: '8px',
      fontWeight: '600',
      zIndex: 1000,
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={styles.container}>
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <div>Loading kitchen orders...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={styles.container}>
        {successMessage && (
          <div style={styles.successMessage}>{successMessage}</div>
        )}

        <div style={styles.header}>
          <h1 style={styles.title}>Kitchen Operations</h1>
          <p style={{ fontSize: isMobile ? '12px' : '14px', color: colors.textLight }}>
            {isMobile ? 'Active orders' : 'Manage active orders and kitchen workflow'}
          </p>
        </div>

        {/* Stats */}
        <div style={styles.statsGrid}>
          <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.warning}` }}>
            <div style={{ ...styles.statValue, color: colors.warning }}>{stats.pendingItems || 0}</div>
            <div style={styles.statLabel}>Pending</div>
          </div>
          <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.accent}` }}>
            <div style={{ ...styles.statValue, color: colors.accent }}>{stats.preparingItems || 0}</div>
            <div style={styles.statLabel}>Preparing</div>
          </div>
          <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.success}` }}>
            <div style={{ ...styles.statValue, color: colors.success }}>{stats.readyItems || 0}</div>
            <div style={styles.statLabel}>Ready</div>
          </div>
          <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.primary}` }}>
            <div style={{ ...styles.statValue, color: colors.primary }}>{stats.completedToday || 0}</div>
            <div style={styles.statLabel}>Completed Today</div>
          </div>
          <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.textLight}` }}>
            <div style={{ ...styles.statValue, color: colors.textLight }}>
              {Math.round(stats.avgPrepTime || 0)} min
            </div>
            <div style={styles.statLabel}>Avg Prep Time</div>
          </div>
        </div>

        {/* Filter Bar */}
        <div style={styles.filterBar}>
          <button
            onClick={() => setSelectedStation('all')}
            style={{
              ...styles.filterButton,
              background: selectedStation === 'all' ? colors.primary : colors.lightGray,
              color: selectedStation === 'all' ? colors.white : colors.textDark
            }}
          >
            All Orders
          </button>
          {stations.map(station => (
            <button
              key={station.id}
              onClick={() => setSelectedStation(station.id)}
              style={{
                ...styles.filterButton,
                background: selectedStation === station.id ? colors.primary : colors.lightGray,
                color: selectedStation === station.id ? colors.white : colors.textDark
              }}
            >
              {station.station_name}
            </button>
          ))}
          <button
            onClick={fetchData}
            style={{
              ...styles.filterButton,
              background: colors.accent,
              color: colors.white,
              marginLeft: 'auto'
            }}
          >
            🔄 Refresh
          </button>
        </div>

        {/* Orders Grid */}
        {activeOrders.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: colors.textLight }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🍽️</div>
            <div>No active orders in kitchen</div>
          </div>
        ) : (
          <div style={styles.ordersGrid}>
            {activeOrders.map(order => (
              <div key={order.id} style={{
                ...styles.orderCard,
                borderColor: getStatusColor(order.item_status)
              }}>
                <div style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  padding: '4px 10px',
                  borderRadius: '6px',
                  fontSize: '10px',
                  fontWeight: '700',
                  background: `${getStatusColor(order.item_status)}20`,
                  color: getStatusColor(order.item_status),
                  textTransform: 'uppercase'
                }}>
                  {order.item_status}
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: '700', color: colors.textDark, marginBottom: '4px' }}>
                    {order.item_name}
                  </div>
                  <div style={{ fontSize: '12px', color: colors.textLight }}>
                    Order #{order.order_number} • Qty: {order.quantity}
                  </div>
                  {order.table_number && (
                    <div style={{ fontSize: '12px', color: colors.textLight }}>
                      Table {order.table_number}
                    </div>
                  )}
                  {order.room_number && (
                    <div style={{ fontSize: '12px', color: colors.textLight }}>
                      Room {order.room_number}
                    </div>
                  )}
                </div>

                <div style={{
                  padding: '8px',
                  background: colors.lightGray,
                  borderRadius: '6px',
                  marginBottom: '12px',
                  fontSize: '11px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ color: colors.textLight }}>Ordered:</span>
                    <span style={{ fontWeight: '600' }}>{getTimeSince(order.order_time)}</span>
                  </div>
                  {order.started_at && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span style={{ color: colors.textLight }}>Started:</span>
                      <span style={{ fontWeight: '600' }}>{getTimeSince(order.started_at)}</span>
                    </div>
                  )}
                  {order.station_name && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span style={{ color: colors.textLight }}>Station:</span>
                      <span style={{ fontWeight: '600' }}>{order.station_name}</span>
                    </div>
                  )}
                  {order.staff_name && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: colors.textLight }}>Staff:</span>
                      <span style={{ fontWeight: '600' }}>{order.staff_name}</span>
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {order.item_status === 'pending' && (
                    <button
                      onClick={() => updateItemStatus(order.id, 'preparing', order.station_id, order.assigned_to_staff_id)}
                      style={{
                        ...styles.button,
                        background: colors.accent,
                        color: colors.white,
                        flex: 1
                      }}
                    >
                      🔥 Start Preparing
                    </button>
                  )}
                  {order.item_status === 'preparing' && (
                    <button
                      onClick={() => updateItemStatus(order.id, 'ready')}
                      style={{
                        ...styles.button,
                        background: colors.success,
                        color: colors.white,
                        flex: 1
                      }}
                    >
                      ✅ Mark Ready
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default KitchenOperations;